// src/components/ui/TeamPosts.tsx
import { useEffect, useState, useRef } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

import { TeamRepository } from "@/infrastructure/repositories/TeamRepository";
import { SharetribeRepository } from "@/infrastructure/repositories/SharetribeRepository";
import { apiService } from "@/services";
import { PostCard } from "./PostCard";

export function TeamPosts({ team, onBack }: any) {
  const repo = new TeamRepository();
  const userCache = useRef<Record<string, any>>({});

  const userRepo = new SharetribeRepository(apiService);

  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const [showPostDialog, setShowPostDialog] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [postText, setPostText] = useState("");
  const [mediaFile, setMediaFile] = useState<File | null>(null);

  const loaderRef = useRef<HTMLDivElement | null>(null);

  const swimmerId = Number(localStorage.getItem("current_user_swimmer_id")) || 0;
  const coachId = Number(localStorage.getItem("coach_id")) || 0;

  // FIX TEAM ID
  const teamId = team.team_id || team.id;

  // --------------------------------------------------
  // LOAD POSTS
  // --------------------------------------------------
  const loadPosts = async (pageNo: number, force = false) => {
  if (!force && (loading || !hasMore)) return;

  setLoading(true);

  const raw = await repo.fetchTeamPosts(teamId, swimmerId, coachId, pageNo);

  if (!raw || raw.length === 0) {
    setHasMore(false);
    setLoading(false);
    return;
  }

  const mapped = raw
    .filter((p) => !deletedPostIds.current.has(p.id))
    .map((p) => ({ ...p, userProfile: null }));

  setPosts((prev) => {
    const merged = pageNo === 1 ? mapped : [...prev, ...mapped];
    return Array.from(new Map(merged.map((p) => [p.id, p])).values());
  });

  setTimeout(() => loadUserProfiles(raw), 0);
  setLoading(false);
};

  const resetPostDialog = () => {
    setPostText("");
    setMediaFile(null);
    setShowEmoji(false);
    setUploadProgress(0);
  };

  // --------------------------------------------------
  // USER PROFILE CACHE
  // --------------------------------------------------
const loadUserProfiles = async (postsPage: any[]) => {
  const userIds = [...new Set(postsPage.map((p) => String(p.user_id)))];

  for (const uid of userIds) {
    if (userCache.current[uid]) {
      setPosts((prev) =>
        prev.map((p) =>
          p.user_id == uid
            ? { ...p, userProfile: userCache.current[uid] }
            : p
        )
      );
      continue;
    }

    const profile =
      (await userRepo.getUserById(Number(uid))) || {
        fname: "Unknown",
        lname: "",
        image: "/placeholder-avatar.jpg",
      };

    userCache.current[uid] = profile;

    setPosts((prev) =>
      prev.map((p) =>
        p.user_id == uid ? { ...p, userProfile: profile } : p
      )
    );
  }
};
  

const resetAndReloadPosts = async () => {
  deletedPostIds.current.clear();
  setPosts([]);
  setHasMore(true);
  setPage(1);

  await loadPosts(1, true);
};




  // --------------------------------------------------
  // PUBLISH NEW POST
  // --------------------------------------------------
  const handlePublishPost = async () => {
    if (!postText.trim() && !mediaFile) return;

    setPosting(true);
    setUploadProgress(10);

    let uploadedPhoto = "";
    let uploadedVideo = "";

    try {
      // ---- IMAGE UPLOAD ----
      if (mediaFile && mediaFile.type.startsWith("image/")) {
        const upload = await repo.uploadTeamPostImage(mediaFile);
        if (upload.status === 1 && upload.image_link) {
          uploadedPhoto = upload.image_link;
        }
        setUploadProgress(100);
      }

      // ---- VIDEO UPLOAD ----
      if (mediaFile && mediaFile.type.startsWith("video/")) {
        const upload = await repo.uploadTeamPostVideo(mediaFile);
        if (upload.status === 1 && upload.video_link) {
          uploadedVideo = upload.video_link;
        }
        setUploadProgress(100);
      }

      const status = await repo.publishTeamPost(
        teamId,
        swimmerId,
        coachId,
        postText.trim(),
        uploadedPhoto,
        uploadedVideo
      );
if (status === 1) {
  setShowPostDialog(false);
  resetPostDialog();

  await resetAndReloadPosts();
}

    } finally {
      setPosting(false);
      setUploadProgress(0);
    }
  };

  // --------------------------------------------------
  // INITIAL LOAD
  // --------------------------------------------------
  useEffect(() => {
  if (page === 1) return;
  loadPosts(page);
}, [page]);


  // --------------------------------------------------
  // INFINITE SCROLL
  // --------------------------------------------------
  useEffect(() => {
    if (!loaderRef.current) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && hasMore) {
          const next = page + 1;
          setPage(next);
          loadPosts(next);
        }
      },
      { threshold: 1 }
    );

    obs.observe(loaderRef.current);
    return () => obs.disconnect();
  }, [loaderRef.current, loading, hasMore]);

const deletedPostIds = useRef<Set<number>>(new Set());

const handleDeleteTeamPost = async (teamFeedId: number) => {
  const prevPosts = posts;

  deletedPostIds.current.add(teamFeedId);

  setPosts((p) => p.filter((x) => x.id !== teamFeedId));

  try {
    const userId =
      Number(localStorage.getItem("current_user_swimmer_id")) || 0;

    const status = await repo.deleteTeamPost(
      teamFeedId,
      teamId,
      userId
    );

    if (status !== 1) {
      deletedPostIds.current.delete(teamFeedId);
      setPosts(prevPosts);
    }
  } catch {
    deletedPostIds.current.delete(teamFeedId);
    setPosts(prevPosts);
  }
};

useEffect(() => {
  deletedPostIds.current.clear();
  setPosts([]);
  setHasMore(true);
  setPage(1);

  loadPosts(1, true);
}, [teamId]);

  // --------------------------------------------------
  // RENDER UI
  // --------------------------------------------------
  return (
    <div className="space-y-4 mt-4">
      <Button variant="outline" onClick={onBack}>
        ← Back to Teams
      </Button>

      <h2 className="text-xl font-semibold">{team.name} - Posts</h2>

      {/* ADD POST BUTTON */}
     <div className="flex justify-end">
  <Button
    size="sm"
    className="flex items-center gap-2 px-4 py-2 text-sm font-medium"
    onClick={() => setShowPostDialog(true)}
  >
    <span className="text-lg leading-none">+</span>
    <span>Add Post</span>
  </Button>
</div>



      {/* CREATE POST DIALOG */}
      <Dialog
  open={showPostDialog}
  onOpenChange={(open) => {
    if (!open) {
      resetPostDialog();
    }
    setShowPostDialog(open);
  }}
>

        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Post</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* TEXT + EMOJI */}
            <div className="relative">
             <textarea
                className="w-full border rounded-lg p-3 text-sm resize-none"
                placeholder="Write something..."
                value={postText}
                rows={3}
                onChange={(e) => setPostText(e.target.value)}
              />


              <button
                className="absolute right-2 bottom-2 text-xl"
                onClick={() => setShowEmoji((v) => !v)}
              >
                😊
              </button>

              {showEmoji && (
                <div className="absolute z-20 bottom-12 right-0 bg-white border rounded-lg shadow-md p-3 grid grid-cols-6 gap-2 text-xl max-h-40 overflow-y-auto">
                  {[
                    "😀",
                    "😄",
                    "😁",
                    "😆",
                    "😊",
                    "😍",
                    "😘",
                    "😎",
                    "🤩",
                    "😇",
                    "😭",
                    "😡",
                    "🔥",
                    "👍",
                    "👎",
                    "🙏",
                    "👏",
                    "💪",
                    "❤️",
                  ].map((emo) => (
                    <button
                      key={emo}
                      className="hover:bg-gray-100 rounded"
                      onClick={() => {
                        setPostText((t) => t + emo);
                        setShowEmoji(false);
                      }}
                    >
                      {emo}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* MEDIA PREVIEW */}
            {mediaFile && (
              <div className="relative">
                {mediaFile.type.startsWith("image/") ? (
                  <img
                    src={URL.createObjectURL(mediaFile)}
                    className="max-h-64 w-full object-cover rounded-lg"
                  />
                ) : (
                  <video
                    src={URL.createObjectURL(mediaFile)}
                    controls
                    className="max-h-64 w-full object-cover rounded-lg"
                  />
                )}

                <Button
                  variant="destructive"
                  className="absolute top-2 right-2 text-xs px-2 py-1"
                  onClick={() => setMediaFile(null)}
                >
                  Remove
                </Button>
              </div>
            )}

            {/* FILE INPUT */}
            <label className="cursor-pointer flex items-center justify-center w-full border border-dashed rounded-lg py-3 hover:bg-gray-50 text-sm text-gray-600 transition">
              <input
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
              />
              {mediaFile ? "Change File" : "Upload Image or Video"}
            </label>

            {/* PROGRESS BAR */}
            {uploadProgress > 0 && (
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            )}

            {/* POST BUTTON */}
            <Button className="w-full" disabled={posting} onClick={handlePublishPost}>
              {posting ? "Posting..." : "Post"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* POSTS */}
      {posts.map((post) => (
        <PostCard
          key={post.id}
          post={post}
          userProfile={post.userProfile}
          currentUserId={swimmerId}
          onDelete={handleDeleteTeamPost}
        />

      ))}

      {loading && 
      <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            </div>
      }

      <div ref={loaderRef} className="h-8" />
    </div>
  );
}
